import { fromBase64, parseShortCode, shortTokenFromSecret, type PairingPayload } from "@foreman/protocol";

export type ShortCodeFailure = "invalid" | "unknown" | "slow_down" | "network";

export class ShortCodeError extends Error {
  constructor(
    readonly code: ShortCodeFailure,
    message: string,
  ) {
    super(message);
  }
}

/**
 * Phone side of the typed pairing code: turn `K7M2-9QXD-4FHP` into the same payload a QR scan gives.
 * Only the first 4 characters (the lookup id) are sent to the relay, which answers with the computer's public key. The
 * remaining 8 characters stay on the phone and become the pairing token that `pairWithDaemon` presents over the encrypted channel.
 * The code resolves on the relay this app talks to (`relayHttp`, the same server as `relayUrl` with https instead of wss).
 */
export async function resolveShortCode(opts: {
  input: string;
  /** The relay's https address. */
  relayHttp: string;
  fetchImpl?: typeof fetch;
  signal?: AbortSignal;
}): Promise<PairingPayload> {
  const code = parseShortCode(opts.input);
  if (!code) throw new ShortCodeError("invalid", "that is not a pairing code");
  const base = opts.relayHttp.replace(/\/+$/, "");
  let res: Response;
  try {
    res = await (opts.fetchImpl ?? fetch)(`${base}/pair/resolve?id=${encodeURIComponent(code.id)}`, { signal: opts.signal });
  } catch {
    throw new ShortCodeError("network", "could not reach the relay");
  }
  if (res.status === 429) throw new ShortCodeError("slow_down", "too many tries");
  if (res.status === 404) throw new ShortCodeError("unknown", "no computer is showing this code");
  if (!res.ok) throw new ShortCodeError("network", `the relay answered ${res.status}`);
  const body = (await res.json().catch(() => null)) as { daemon?: unknown; expiresAt?: unknown } | null;
  let daemonPublicKey: Uint8Array;
  try {
    daemonPublicKey = fromBase64(String(body?.daemon));
  } catch {
    throw new ShortCodeError("network", "the relay sent an answer this app does not understand");
  }
  if (daemonPublicKey.length !== 32 || typeof body?.expiresAt !== "number") throw new ShortCodeError("network", "the relay sent an answer this app does not understand");
  return {
    relayUrl: base.replace(/^http/, "ws"),
    daemonPublicKey,
    token: shortTokenFromSecret(code.secret),
    expiresAt: body.expiresAt,
  };
}
